import { formatarDia, Periodo, somarDias } from './Dashboards.periodo'
import {
    AGUARDAR_COM_PRAZO,
    chaveDoCaminho,
    type Consultas,
    type FilhosParceiro,
    type FiltrosConsultas,
    FILTROS_CONSULTAS_VAZIOS,
    type Metricas,
    type NoParceiro
} from './Dashboards.scopes'
import { COR } from './Operacao.graficos'

// Peças do dashboard de consultas de lead (E19), o que substitui o Power BI "Automação Comercial - AG".

export const numero = (n: number) => n.toLocaleString('pt-BR')

/** R$ 396.576,00 */
export const moeda = (n: number) => n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })

/** R$ 140.529, como a coluna "Investimento" do Power BI. */
export const moedaInteira = (n: number) =>
    n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 })

const MESES = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez']

/** 2026-10 → Out/2026; 2026-10-22 → 22/10. */
export function rotuloDaChave(chave: string, comAno = false): string {
    if (chave.length === 7) {
        const [ano, mes] = chave.split('-')
        return `${MESES[Number(mes) - 1]}/${ano}`
    }
    return formatarDia(chave, comAno)
}

/** Todos os dias ou meses do período, para a série não pular os que não tiveram consulta. */
export function chavesDoPeriodo(periodo: Periodo, granularidade: 'dia' | 'mes'): string[] {
    const chaves: string[] = []
    if (granularidade === 'dia') {
        for (let dia = periodo.de; dia <= periodo.ate; dia = somarDias(dia, 1)) chaves.push(dia)
        return chaves
    }
    let [ano, mes] = periodo.de.split('-').map(Number)
    const fim = periodo.ate.slice(0, 7)
    for (;;) {
        const chave = `${ano}-${`${mes}`.padStart(2, '0')}`
        if (chave > fim) break
        chaves.push(chave)
        mes++
        if (mes > 12) {
            mes = 1
            ano++
        }
    }
    return chaves
}

/**
 * "Total de CNPJs no período": a linha pequena ao lado dos totais, por mês (ou por dia em períodos curtos). Com
 * `eixos`, para o PDF, mostra os meses e a escala.
 */
export function opcaoSerie(consultas: Pick<Consultas, 'serie' | 'granularidade' | 'periodo'>, eixos = false) {
    const chaves = chavesDoPeriodo(consultas.periodo, consultas.granularidade)
    const porChave = new Map(consultas.serie.map((s) => [s.chave, s.consultas]))
    return {
        grid: eixos
            ? { left: 8, right: 16, top: 16, bottom: 8, containLabel: true }
            : { left: 6, right: 6, top: 8, bottom: 4, containLabel: false },
        tooltip: {
            trigger: 'axis',
            formatter: (itens: { dataIndex: number; value: number }[]) =>
                `${rotuloDaChave(chaves[itens[0].dataIndex], true)}<br/>${numero(itens[0].value)} consultas`
        },
        xAxis: {
            type: 'category',
            data: chaves.map((c) => rotuloDaChave(c)),
            show: eixos,
            boundaryGap: false,
            axisLine: { lineStyle: { color: COR.eixo } },
            axisTick: { show: false },
            axisLabel: { color: COR.texto, hideOverlap: true }
        },
        yAxis: {
            type: 'value',
            show: eixos,
            min: 0,
            splitLine: { lineStyle: { color: COR.grade } },
            axisLabel: { color: COR.texto, formatter: (v: number) => numero(v) }
        },
        series: [
            {
                name: 'Consultas',
                type: 'line',
                symbol: 'circle',
                symbolSize: chaves.length > 40 ? 0 : 6,
                lineStyle: { color: COR.serie1, width: 2 },
                itemStyle: { color: COR.serie1 },
                data: chaves.map((c) => porChave.get(c) ?? 0)
            }
        ]
    }
}

export type DivisaoCnae = {
    divisao: string
    consultas: number
    cadastros: number
    descricoes: { descricao: string; consultas: number; cadastros: number }[]
}

/** Consultas por divisão do CNAE, com as descrições dentro, da que mais consultou para a que menos. */
export function agruparCnae(linhas: Consultas['cnae']): DivisaoCnae[] {
    const porDivisao = new Map<string, DivisaoCnae>()
    for (const l of linhas) {
        const divisao = l.divisao || 'Sem CNAE no cadastro'
        const d = porDivisao.get(divisao) ?? { divisao, consultas: 0, cadastros: 0, descricoes: [] }
        d.consultas += l.consultas
        d.cadastros += l.cadastros
        d.descricoes.push({ descricao: l.descricao || '—', consultas: l.consultas, cadastros: l.cadastros })
        porDivisao.set(divisao, d)
    }
    const ordem = (a: { consultas: number }, b: { consultas: number }) => b.consultas - a.consultas
    return [...porDivisao.values()].map((d) => ({ ...d, descricoes: d.descricoes.sort(ordem) })).sort(ordem)
}

// :: Árvore de parceiros ("Solicitações na LIA por Parceiro")

export type LinhaDaArvore =
    | { tipo: 'no'; chave: string; caminho: string[]; nivel: number; no: NoParceiro; aberto: boolean }
    | { tipo: 'proprias'; chave: string; nivel: number; nome: string; metricas: Metricas }

/**
 * As linhas visíveis da árvore, em ordem: cada nó aberto traz embaixo os filhos e, se houver, as consultas que o
 * próprio parceiro fez (que entram no total dele, mas não em nenhum filho).
 */
export function linhasDaArvore(
    raiz: NoParceiro[],
    abertos: readonly string[],
    filhos: Record<string, FilhosParceiro>
): LinhaDaArvore[] {
    const abertosSet = new Set(abertos)
    const linhas: LinhaDaArvore[] = []
    const visitar = (nos: NoParceiro[], acima: string[]) => {
        for (const no of nos) {
            const caminho = [...acima, no.ownerId]
            const chave = chaveDoCaminho(caminho)
            const carregado = filhos[chave]
            const aberto = abertosSet.has(chave) && Boolean(carregado)
            linhas.push({ tipo: 'no', chave, caminho, nivel: acima.length, no, aberto })
            if (!aberto || !carregado) continue
            if (carregado.proprias && carregado.proprias.consultas > 0) {
                linhas.push({
                    tipo: 'proprias',
                    chave: `${chave}#proprias`,
                    nivel: caminho.length,
                    nome: no.nome,
                    metricas: carregado.proprias
                })
            }
            visitar(carregado.filhos, caminho)
        }
    }
    visitar(raiz, [])
    return linhas
}

// :: Listas coladas (o "Filtro de Lista" do Power BI)

/** Itens de uma lista colada, separados por linha, espaço, vírgula ou ponto e vírgula. */
export function itensDaLista(texto: string): string[] {
    return texto
        .split(/[\s,;]+/)
        .map((t) => t.trim())
        .filter(Boolean)
}

/** Mesma regra do servidor: aceita a máscara e repõe os zeros à esquerda que a planilha come. */
export function normalizarCnpj(texto: string): string | undefined {
    const digitos = texto.replace(/\D/g, '')
    if (digitos.length < 11 || digitos.length > 14) return undefined
    return digitos.padStart(14, '0')
}

export function cnpjsDaLista(texto: string): { validos: string[]; invalidos: string[] } {
    const validos = new Set<string>()
    const invalidos: string[] = []
    for (const item of itensDaLista(texto)) {
        const cnpj = normalizarCnpj(item)
        if (cnpj) validos.add(cnpj)
        else invalidos.push(item)
    }
    return { validos: [...validos], invalidos }
}

export function negociosDaLista(texto: string): { validos: string[]; invalidos: string[] } {
    const validos = new Set<string>()
    const invalidos: string[] = []
    for (const item of itensDaLista(texto)) {
        if (/^\d{1,19}$/.test(item)) validos.add(item.replace(/^0+(?=\d)/, ''))
        else invalidos.push(item)
    }
    return { validos: [...validos], invalidos }
}

/** Uma lista de volta em texto, um por linha, para editar. */
export const listaEmTexto = (itens: string[]) => itens.join('\n')

// :: Etiquetas dos filtros

/** SP|SAO PAULO → SAO PAULO/SP */
export function rotuloMunicipio(valor: string): string {
    const [uf, ...resto] = valor.split('|')
    return `${resto.join('|')}/${uf}`
}

export type GrupoDeFiltro = Exclude<keyof FiltrosConsultas, never>

export type Etiqueta = { grupo: GrupoDeFiltro; rotulo: string }

const resumir = (titulo: string, itens: string[], maximo = 3) =>
    `${titulo}: ${itens.slice(0, maximo).join(', ')}${itens.length > maximo ? ` e mais ${itens.length - maximo}` : ''}`

/** Uma etiqueta por filtro em uso, na ordem do painel de filtros. */
export function etiquetasDosFiltros(f: FiltrosConsultas, nomes: Record<string, string> = {}): Etiqueta[] {
    const nome = (ownerId: string) => nomes[ownerId] ?? ownerId
    const etiquetas: Etiqueta[] = []
    if (f.rede) etiquetas.push({ grupo: 'rede', rotulo: `Rede de ${nome(f.rede)}` })
    if (f.parceiros.length)
        etiquetas.push({ grupo: 'parceiros', rotulo: resumir('Parceiro', f.parceiros.map(nome), 2) })
    if (f.classificacoes.length)
        etiquetas.push({ grupo: 'classificacoes', rotulo: resumir('Desfecho', f.classificacoes) })
    if (f.ufs.length) etiquetas.push({ grupo: 'ufs', rotulo: resumir('UF', f.ufs, 6) })
    if (f.municipios.length) {
        etiquetas.push({ grupo: 'municipios', rotulo: resumir('Município', f.municipios.map(rotuloMunicipio), 2) })
    }
    if (f.secoes.length) etiquetas.push({ grupo: 'secoes', rotulo: resumir('Seção do CNAE', f.secoes, 1) })
    if (f.divisoes.length) etiquetas.push({ grupo: 'divisoes', rotulo: resumir('Divisão do CNAE', f.divisoes, 1) })
    if (f.cnae) etiquetas.push({ grupo: 'cnae', rotulo: `CNAE: “${f.cnae}”` })
    if (f.naturezas.length) etiquetas.push({ grupo: 'naturezas', rotulo: resumir('Natureza jurídica', f.naturezas, 1) })
    if (f.cnpjs.length) {
        etiquetas.push({
            grupo: 'cnpjs',
            rotulo: f.cnpjs.length === 1 ? `CNPJ ${f.cnpjs[0]}` : `${numero(f.cnpjs.length)} CNPJs da lista`
        })
    }
    if (f.negocios.length) {
        etiquetas.push({
            grupo: 'negocios',
            rotulo:
                f.negocios.length === 1 ? `Negócio ${f.negocios[0]}` : `${numero(f.negocios.length)} negócios da lista`
        })
    }
    return etiquetas
}

/** Os filtros sem um grupo. */
export function semGrupo(f: FiltrosConsultas, grupo: GrupoDeFiltro): FiltrosConsultas {
    return { ...f, [grupo]: FILTROS_CONSULTAS_VAZIOS[grupo] }
}

export const temFiltro = (f: FiltrosConsultas) => etiquetasDosFiltros(f).length > 0

/** Acrescenta um valor a um grupo de filtro em lista (clique no mapa, no CNAE, numa empresa). */
export function comValor(
    f: FiltrosConsultas,
    grupo: 'municipios' | 'divisoes' | 'cnpjs' | 'ufs',
    valor: string
): FiltrosConsultas {
    return f[grupo].includes(valor) ? f : { ...f, [grupo]: [...f[grupo], valor] }
}

/** Rótulo do desfecho nas opções do filtro. */
export const rotuloClassificacao = (valor: string) =>
    valor === AGUARDAR_COM_PRAZO ? 'Aguardar N dias (todos os prazos)' : valor

/** Raio do círculo no mapa: a área cresce com os CNPJs consultados no município. */
export function raioDoPonto(cnpjs: number, maximo: number): number {
    if (maximo <= 0) return 3
    return 3 + 19 * Math.sqrt(cnpjs / maximo)
}
